import { useMemo, type ReactNode } from 'react';
import { useCreate, useMeta, useResource, useUpdate } from '../api/hooks';
import { AutoForm, type AutoFormProps } from '../forms/AutoForm';
import { Drawer, Dialog } from '../design/primitives/Overlay';
import { ErrorBanner, Loading, PageHeader, Skeleton } from '../design/primitives/Misc';
import { orderedFields, type ResourceMeta } from '../meta/types';
import type { ScreenDef } from './types';

// ResourceFormScreen: create or edit a record from its meta. Renders inside a Drawer
// (default), a Dialog, or as a page; the form itself is AutoForm. Custom form components
// (ScreenDef.form.component) replace AutoForm while keeping the chrome.

export interface ResourceFormScreenProps {
  resource: string;
  /** Editing an existing record. */
  id?: string;
  mode?: 'drawer' | 'dialog' | 'page' | 'inline';
  open?: boolean;
  onClose: () => void;
  onDone?: (row: Record<string, unknown>) => void;
  /** Field subset / order override. */
  fields?: string[];
  initial?: Record<string, unknown>;
  /**
   * Create mode: fields fixed by the caller's context. Defaults to every non-empty key of
   * `initial` (a related list's parent filter, a detail screen's `{ project_id }`), which the
   * form then shows read-only instead of asking for again. Pass `[]` to keep them editable.
   */
  locked?: readonly string[];
  title?: ReactNode;
  description?: ReactNode;
  form?: ScreenDef['form'];
  conditions?: AutoFormProps['conditions'];
  size?: 'md' | 'lg';
}

export function formFields(meta: ResourceMeta, mode: 'create' | 'edit', subset?: string[]) {
  const all = orderedFields(meta).filter((f) => (mode === 'create' ? f.inCreate : f.inUpdate) && !f.readOnly);
  if (!subset) return all;
  return subset.map((n) => all.find((f) => f.name === n)).filter((f): f is NonNullable<typeof f> => Boolean(f));
}

export function ResourceFormScreen({ resource, id, mode = 'drawer', open = true, onClose, onDone, fields, initial, locked, title, description, form, conditions, size = 'md' }: ResourceFormScreenProps) {
  const meta = useMeta(resource);
  const editing = Boolean(id);
  const row = useResource(resource, id, { enabled: editing });
  const create = useCreate(resource, { toast: { success: (r) => `${meta.data?.label ?? 'Record'} ${labelOf(meta.data, r as Record<string, unknown>)} created` } });
  const update = useUpdate(resource, { toast: { success: 'Saved' } });
  const fieldList = useMemo(() => (meta.data ? formFields(meta.data, editing ? 'edit' : 'create', fields ?? form?.fields) : []), [meta.data, editing, fields, form?.fields]);

  const lockedFields = useMemo(
    () => (editing ? [] : locked ?? Object.entries(initial ?? {}).filter(([, v]) => v != null && v !== '').map(([k]) => k)),
    [editing, locked, initial],
  );

  const heading = title ?? (meta.data ? (editing ? `Edit ${meta.data.label.toLowerCase()}` : `New ${meta.data.label.toLowerCase()}`) : editing ? 'Edit' : 'New');

  const body: ReactNode = meta.isLoading || (editing && row.isLoading) ? (
    <Skeleton lines={5} />
  ) : meta.error ? (
    <ErrorBanner error={meta.error} />
  ) : row.error ? (
    <ErrorBanner error={row.error} />
  ) : form?.component ? (
    <form.component id={id} onDone={(r) => (r ? onDone?.(r) : onClose())} onCancel={onClose} />
  ) : (
    <AutoForm
      fields={fieldList}
      mode={editing ? 'edit' : 'create'}
      collapseSecondary={!editing}
      initial={editing ? (row.data as Record<string, unknown>) : initial}
      locked={lockedFields}
      conditions={conditions}
      onCancel={onClose}
      submitLabel={editing ? 'Save changes' : `Create ${meta.data?.label.toLowerCase() ?? ''}`}
      busy={create.isPending || update.isPending}
      onSubmit={async (values) => {
        if (editing) {
          const previous: Record<string, unknown> = {};
          for (const k of Object.keys(values)) previous[k] = (row.data as Record<string, unknown> | undefined)?.[k];
          const r = await update.mutateAsync({ id: id!, body: values, previous });
          onDone?.(r as Record<string, unknown>);
        } else {
          // Context the caller fixed but the form does not show (a detail screen's
          // `initial={{ project_id }}` with a `form.fields` subset) still has to reach the API.
          const hidden: Record<string, unknown> = {};
          for (const [k, v] of Object.entries(initial ?? {})) if (v != null && !fieldList.some((f) => f.name === k)) hidden[k] = v;
          const r = await create.mutateAsync({ ...hidden, ...values });
          onDone?.(r as Record<string, unknown>);
        }
      }}
    />
  );

  if (mode === 'inline') return <>{body}</>;
  if (mode === 'page') {
    return (
      <div className="hf-page">
        <PageHeader title={heading} subtitle={description} compact />
        <div style={{ maxWidth: 720 }}>{body}</div>
      </div>
    );
  }
  if (mode === 'dialog') {
    return (
      <Dialog open={open} onClose={onClose} title={heading} description={description} size={size === 'lg' ? 'lg' : 'md'} locked={create.isPending || update.isPending}>
        {body}
      </Dialog>
    );
  }
  return (
    <Drawer open={open} onClose={onClose} title={heading} description={description} size={size} locked={create.isPending || update.isPending}>
      {meta.isLoading ? <Loading /> : body}
    </Drawer>
  );
}

export function labelOf(meta: ResourceMeta | undefined, row: Record<string, unknown> | undefined): string {
  if (!row) return '';
  const v = meta ? row[meta.displayField] : undefined;
  if (v != null && v !== '') return String(v);
  return String(row.name ?? row.ref ?? row.number ?? row.title ?? (row.id ? String(row.id).slice(0, 8) : ''));
}
